'use client';

import React from 'react';
import { Info, AlertTriangle, CheckCircle, X } from '@/lib/icons';
import * as LucideIcons from '@/lib/icons';

interface AlertProps {
  children: React.ReactNode;
  type?: 'info' | 'warning' | 'success' | 'error';
  icon?: string;
  dismissible?: boolean;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({ 
  children, 
  type = 'info',
  icon,
  dismissible = false,
  className = '' 
}) => {
  const [isVisible, setIsVisible] = React.useState(true);

  if (!isVisible) return null;

  const typeStyles = {
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      iconColor: 'text-blue-600',
      defaultIcon: Info
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      iconColor: 'text-yellow-600',
      defaultIcon: AlertTriangle
    },
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      iconColor: 'text-green-600',
      defaultIcon: CheckCircle
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      iconColor: 'text-red-600',
      defaultIcon: AlertTriangle
    }
  };

  const style = typeStyles[type];
  
  // Safety check: if style is undefined, fallback to info type
  if (!style) {
    console.warn(`Alert: Invalid type '${type}'. Using 'info' as fallback.`);
    const fallbackStyle = typeStyles.info;
    return (
      <div className={`${fallbackStyle.bg} ${fallbackStyle.border} border rounded-md p-3 lg:p-4 my-6 mdx-component-base ${className}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Info className={`w-4 lg:w-5 h-4 lg:h-5 ${fallbackStyle.iconColor}`} />
          </div>
          <div className={`ml-2 lg:ml-3 flex-1 ${fallbackStyle.text} component-body`} style={{ textWrap: 'pretty' }}>
            {children}
          </div>
        </div>
      </div>
    );
  }
  
  // Use provided icon or default based on type
  let IconComponent = style.defaultIcon;
  if (icon) {
    const iconName = icon.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('');
    IconComponent = (LucideIcons as any)[iconName] || style.defaultIcon;
  }

  return (
    <div className={`${style.bg} ${style.border} border rounded-md p-3 lg:p-4 my-6 mdx-component-base ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <IconComponent className={`w-4 lg:w-5 h-4 lg:h-5 ${style.iconColor}`} />
        </div>
        <div className={`ml-2 lg:ml-3 flex-1 ${style.text} component-body`} style={{ textWrap: 'pretty' }}>
          {children}
        </div>
        {dismissible && (
          <button
            onClick={() => setIsVisible(false)}
            className={`ml-3 flex-shrink-0 ${style.iconColor} hover:opacity-70 transition-opacity`}
            aria-label="Dismiss"
          >
            <X className="w-4 lg:w-5 h-4 lg:h-5" />
          </button>
        )}
      </div>
    </div>
  );
};
